// frontend/src/components/CartaoDespesaModal/CartaoDespesaModal.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Form, Input, Select, DatePicker, Radio, Button, Alert, InputNumber, Typography, AutoComplete, Row, Col, App } from 'antd';
import { getCategorias } from '../../services/categoryService';
import { getDescricaoSuggestions, deleteDescricaoSuggestion } from '../../services/suggestionService';
import { mapDescricaoSuggestions } from '../../utils/descricaoSuggestions';
import { 
    getFaturasDisponiveis, 
    getCartoesAdicionais, 
    createTransacaoFatura,
    createCompraParcelada,
    createEncargoFatura,
    updateEncargoFatura
} from '../../services/cardService';
import { updateTransacao } from '../../services/transactionService';
import dayjs from 'dayjs';
import { getErrorMessage } from '../../utils/errorHandler';
import { modalPopupContainer, scrollFieldIntoView } from '../../utils/mobileModal';
import { currencyInputProps } from '../../utils/numberInputMask';
import MobileLaunchSheet from '../MobileLaunchSheet/MobileLaunchSheet';

const { Option } = Select;
const { Text } = Typography;

const moneyInputProps = currencyInputProps({ inputMode: 'decimal' });

const ENCARGO_CHOICES = {
    'ANUIDADE': 'Anuidade', 'JUROS': 'Juros', 'MULTA': 'Multa por Atraso',
    'IOF': 'IOF', 'SAQUE': 'Taxa de Saque',
    'AVALIACAO_CREDITO': 'Avaliação Emergencial de Crédito', 'OUTROS': 'Outros Encargos',
};

const parseDescricaoParaEdicao = (descricao) => {
    if (!descricao) return { tipo_encargo: 'OUTROS', descricao: '' };
    for (const key in ENCARGO_CHOICES) {
        const label = `${ENCARGO_CHOICES[key]}: `;
        if (descricao.startsWith(label)) return { tipo_encargo: key, descricao: descricao.substring(label.length) };
    }
    if (Object.values(ENCARGO_CHOICES).includes(descricao)) {
        for (const key in ENCARGO_CHOICES) {
            if (ENCARGO_CHOICES[key] === descricao) return { tipo_encargo: key, descricao: '' };
        }
    }
    return { tipo_encargo: 'OUTROS', descricao: descricao };
};

/** Extra do parcelado — useWatch isolado para não re-renderizar o modal pai a cada tecla. */
function ParceladoValorExtra({ form, isEditMode }) {
    const valorTotal = Form.useWatch('valor', form);
    const totalParcelas = Form.useWatch('total_parcelas', form);
    const valorDaParcela = (valorTotal > 0 && totalParcelas > 1) ? (valorTotal / totalParcelas) : 0;
    if (isEditMode || valorDaParcela <= 0) return null;
    return (
        <Text type="secondary">
            Valor da parcela: {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(valorDaParcela)}
        </Text>
    );
}

function FaturaField({
    isParcelado = false,
    faturasDisponiveis,
    isLoading,
    onFaturaChange,
    filterOption,
    disabled,
    inputSize,
}) {
    return (
        <Form.Item
            name="fatura_id"
            label={isParcelado ? 'Fatura da 1ª Parcela' : 'Fatura'}
            rules={[{ required: true, message: 'Fatura é obrigatória' }]}
        >
            <Select
                placeholder="Selecione uma fatura..."
                loading={isLoading}
                onChange={onFaturaChange}
                showSearch
                filterOption={filterOption}
                disabled={disabled}
                size={inputSize}
                getPopupContainer={modalPopupContainer}
            >
                {faturasDisponiveis.map((f) => (
                    <Option key={f.id} value={f.id}>{f.nome}</Option>
                ))}
            </Select>
        </Form.Item>
    );
}

// 1. RECEBE A PROP isMobile
const CartaoDespesaModal = ({ isOpen, onClose, onSave, transacaoToEdit, cartaoId, faturaSugeridaId, escopoDaAlteracao, isMobile = false }) => {
    const [form] = Form.useForm();
    const { modal, message } = App.useApp();
    const [error, setError] = useState('');
    const [tipoLancamento, setTipoLancamento] = useState('vista');
    const [categoriasDespesa, setCategoriasDespesa] = useState([]);
    const [faturasDisponiveis, setFaturasDisponiveis] = useState([]);
    const [cartoesAdicionais, setCartoesAdicionais] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [selectedFaturaStatus, setSelectedFaturaStatus] = useState(null);
    const [isSavingAndNew, setIsSavingAndNew] = useState(false);
    
    const [suggestions, setSuggestions] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');

    const handleRemoverSugestao = useCallback((sugestao) => {
        modal.confirm({
            title: 'Remover sugestão',
            content: `"${sugestao.descricao}" não vai mais aparecer nas sugestões de descrição. Lançamentos já salvos não são afetados.`,
            okText: 'Remover',
            okButtonProps: { danger: true },
            cancelText: 'Cancelar',
            onOk: async () => {
                try {
                    await deleteDescricaoSuggestion(sugestao.id);
                    setSuggestions((prev) => prev.filter((o) => o.id !== sugestao.id));
                    message.success('Sugestão removida.');
                } catch (err) {
                    message.error(getErrorMessage(err, 'Não foi possível remover a sugestão.'));
                }
            },
        });
    }, [modal, message]);

    useEffect(() => {
        if (searchTerm.length < 2) {
            setSuggestions([]);
            return;
        }

        const fetchSuggestions = async () => {
            const results = await getDescricaoSuggestions(searchTerm);
            setSuggestions(mapDescricaoSuggestions(results, { onDelete: handleRemoverSugestao }));
        };

        const delayDebounceFn = setTimeout(() => {
            fetchSuggestions();
        }, 300);

        return () => clearTimeout(delayDebounceFn);
    }, [searchTerm, handleRemoverSugestao]);

    const isEditMode = !!transacaoToEdit;
    const isParceladoEditMode = isEditMode && (transacaoToEdit?.compra_original || transacaoToEdit?.compra_parcelada_id);
    
    useEffect(() => {
        const initializeModal = async () => {
            if (!isOpen) {
                setSuggestions([]);
                setSearchTerm('');
                return;
            }

            setIsLoading(true);
            setError('');
            form.resetFields();
            setTipoLancamento('vista');
            setSelectedFaturaStatus(null);

            try {
                const [catData, fatData, cartoesAdData] = await Promise.all([
                    getCategorias(),
                    getFaturasDisponiveis(cartaoId, faturaSugeridaId),
                    getCartoesAdicionais(cartaoId, null)
                ]);

                setCategoriasDespesa(catData.filter(c => c.tipo === 'despesa').sort((a, b) => a.nome.localeCompare(b.nome)));
                setFaturasDisponiveis(fatData);
                setCartoesAdicionais(cartoesAdData.sort((a, b) => a.portador.localeCompare(b.portador)));

                if (isEditMode) {
                    const dataParaFormulario = transacaoToEdit.data_vencimento ? dayjs(transacaoToEdit.data_vencimento) : null;
                    const faturaDaTransacao = fatData.find(f => f.id === transacaoToEdit.fatura);
                    setSelectedFaturaStatus(faturaDaTransacao ? faturaDaTransacao.status : null);

                    const portadorValue = transacaoToEdit.cartao_adicional || 'TITULAR';

                    if (isParceladoEditMode) {
                        setTipoLancamento('parcelado');
                        form.setFieldsValue({
                            ...transacaoToEdit,
                            descricao: transacaoToEdit.descricao.replace(/\s\(\d+\/\d+\)$/, ''),
                            tipoLancamento: 'parcelado',
                            data: dataParaFormulario,
                            fatura_id: transacaoToEdit.fatura,
                            realizado_por_cartao: portadorValue,
                        });
                    } else if (transacaoToEdit.tipo_transacao_cartao === 'ENCARGO') {
                        setTipoLancamento('encargo');
                        const { tipo_encargo, descricao } = parseDescricaoParaEdicao(transacaoToEdit.descricao);
                        form.setFieldsValue({
                            ...transacaoToEdit,
                            tipoLancamento: 'encargo',
                            data: dataParaFormulario,
                            fatura_id: transacaoToEdit.fatura,
                            tipo_encargo,
                            descricao_encargo: descricao,
                        });
                    } else {
                        setTipoLancamento('vista');
                        form.setFieldsValue({
                            ...transacaoToEdit,
                            tipoLancamento: 'vista',
                            data: dataParaFormulario,
                            fatura_id: transacaoToEdit.fatura,
                            realizado_por_cartao: portadorValue,
                        });
                    }
                } else {
                    let faturaPadraoId = faturaSugeridaId;
                    if (!faturaPadraoId && fatData.length > 0) {
                        const proximaFaturaAberta = fatData.find(f => f.status === 'ABERTA');
                        if (proximaFaturaAberta) {
                            faturaPadraoId = proximaFaturaAberta.id;
                        } else {
                            const faturaContextual = fatData.find(f => f.id === faturaSugeridaId);
                            faturaPadraoId = faturaContextual ? faturaContextual.id : fatData[0]?.id;
                        }
                    }

                    form.setFieldsValue({
                        realizado_por_cartao: 'TITULAR', 
                        data: dayjs(),
                        fatura_id: faturaPadraoId,
                    });
                    
                    const faturaInicial = fatData.find(f => f.id === faturaPadraoId);
                    setSelectedFaturaStatus(faturaInicial ? faturaInicial.status : null);
                }

            } catch (err) {
                setError(getErrorMessage(err, "Falha ao carregar dados do modal."));
            } finally {
                setIsLoading(false);
            }
        };

        initializeModal();

    }, [isOpen, cartaoId, faturaSugeridaId, form, isEditMode, transacaoToEdit, isParceladoEditMode]);


    const handleSearch = (value) => { setSearchTerm(value); };

    const handleSelect = (value, option) => {
        setSearchTerm(value);
        form.setFieldsValue({
            descricao: option.value,
            categoria: option.categoria_id,
        });
    };

    const buildPayload = (values) => {
        const dataFormatada = values.data ? dayjs(values.data).format('YYYY-MM-DD') : null;
        const isParcelado = tipoLancamento === 'parcelado';
        const isEncargo = tipoLancamento === 'encargo';

        const cartaoAdicionalId = (values.realizado_por_cartao && values.realizado_por_cartao !== 'TITULAR') 
            ? values.realizado_por_cartao 
            : null;

        if (isEncargo) {
            return {
                tipo_encargo: values.tipo_encargo,
                descricao: values.descricao_encargo,
                valor: values.valor,
                data_vencimento: dataFormatada,
                fatura_id: values.fatura_id,
            };
        }
        
        if (isParcelado) {
            if (isEditMode) {
                return {
                    valor: values.valor,
                    categoria: values.categoria,
                    cartao_adicional: cartaoAdicionalId,
                    escopo_da_alteracao: escopoDaAlteracao,
                };
            }
            return {
                descricao: values.descricao,
                valor_total: values.valor,
                total_parcelas: values.total_parcelas,
                data_compra: dataFormatada,
                categoria_id: values.categoria,
                cartao_id: cartaoId,
                fatura_inicial_id: values.fatura_id,
                cartao_adicional_id: cartaoAdicionalId,
            };
        }
        
        return {
            descricao: values.descricao,
            valor: values.valor,
            data_vencimento: dataFormatada,
            categoria: values.categoria,
            fatura: values.fatura_id,
            tipo: 'despesa',
            cartao_adicional: cartaoAdicionalId,
        };
    };

    const handleApiCall = async (payload) => {
        const isParcelado = tipoLancamento === 'parcelado';
        const isEncargo = tipoLancamento === 'encargo';

        if (isEncargo) {
            return isEditMode ? updateEncargoFatura(transacaoToEdit.id, payload) : createEncargoFatura(payload);
        } else if (isParcelado) {
            return isEditMode ? updateTransacao(transacaoToEdit.id, payload) : createCompraParcelada(payload);
        } else {
            return isEditMode ? updateTransacao(transacaoToEdit.id, payload) : createTransacaoFatura(payload);
        }
    };
    
    const onFinish = async (values) => {
        setError('');
        setIsLoading(true);

        try {
            const payload = buildPayload(values);
            await handleApiCall(payload);
            onSave(true, 'Lançamento salvo com sucesso!');
        } catch (err) {
            setError(getErrorMessage(err, 'Ocorreu um erro ao salvar.'));
        } finally {
            setIsLoading(false);
        }
    };

    const handleSaveAndNew = async () => {
        try {
            const values = await form.validateFields();
            setError('');
            setIsSavingAndNew(true);

            const payload = buildPayload(values);
            await handleApiCall(payload);
            
            onSave(false, 'Lançamento criado com sucesso!');

            form.resetFields(['descricao', 'valor', 'categoria', 'total_parcelas', 'descricao_encargo']);
            setSearchTerm('');
            const currentPortador = values.realizado_por_cartao;
            const currentData = values.data;
            form.setFieldsValue({
                realizado_por_cartao: currentPortador,
                data: currentData
            });
            
        } catch (err) {
            if (err?.errorFields) return;
            setError(getErrorMessage(err, 'Ocorreu um erro ao salvar.'));
        } finally {
            setIsSavingAndNew(false);
        }
    };

    const handleCancel = () => { !isLoading && onClose(); };

    const filterOption = (input, option) => (option?.children ?? '').toLowerCase().includes(input.toLowerCase());
    const handleFaturaChange = (faturaId) => {
        const faturaSelecionada = faturasDisponiveis.find(f => f.id === faturaId);
        setSelectedFaturaStatus(faturaSelecionada ? faturaSelecionada.status : null);
    };

    // 2. ESTILOS RESPONSIVOS
    const inputSize = isMobile ? 'large' : undefined;
    const modalWidth = isMobile ? '100%' : 520;
    const sheetTitle = isEditMode ? 'Editar Lançamento' : 'Novo Lançamento no Cartão';
    const saveBusy = isLoading || isSavingAndNew;

    const faturaFieldProps = {
        faturasDisponiveis,
        isLoading,
        onFaturaChange: handleFaturaChange,
        filterOption,
        disabled: isParceladoEditMode,
        inputSize,
    };

    const fechadaAlert = selectedFaturaStatus === 'FECHADA' ? (
        <Alert
            message="Lançamento em fatura fechada."
            type="warning"
            showIcon
            style={{ marginTop: -8, marginBottom: 16 }}
        />
    ) : null;

    const renderFormContent = () => {
        if (tipoLancamento === 'encargo') {
            return (
                <>
                    <Form.Item name="tipo_encargo" label="Tipo de Encargo" initialValue="JUROS" rules={[{ required: true, message: 'Tipo é obrigatório' }]}>
                        <Select showSearch placeholder="Selecione o tipo" optionFilterProp="children" filterOption={filterOption} size={inputSize} getPopupContainer={modalPopupContainer}>
                            {Object.entries(ENCARGO_CHOICES).sort(([, a], [, b]) => a.localeCompare(b)).map(([key, value]) => <Option key={key} value={key}>{value}</Option>)}
                        </Select>
                    </Form.Item>
                    <Form.Item name="valor" label="Valor" rules={[{ required: true, message: 'Valor é obrigatório' }]}>
                        <InputNumber size={inputSize} {...moneyInputProps} onFocus={scrollFieldIntoView} />
                    </Form.Item>
                    <Form.Item name="descricao_encargo" label="Descrição Adicional (Opcional)">
                        <Input size={inputSize} onFocus={scrollFieldIntoView} />
                    </Form.Item>
                    <Form.Item name="data" label="Data do Lançamento" rules={[{ required: true, message: 'Data é obrigatória' }]}>
                        <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} size={inputSize} getPopupContainer={modalPopupContainer} onFocus={scrollFieldIntoView} />
                    </Form.Item>
                    <FaturaField {...faturaFieldProps} />
                    {fechadaAlert}
                </>
            );
        }

        // vista + parcelado: campos comuns sempre montados (trocar tipo não remonta valor/descrição)
        const isParcelado = tipoLancamento === 'parcelado';
        return (
            <>
                <Form.Item name="descricao" label="Descrição" rules={[{ required: true, message: 'Descrição é obrigatória' }]}>
                    <AutoComplete
                        options={suggestions}
                        onSearch={handleSearch}
                        onSelect={handleSelect}
                        filterOption={false}
                        placeholder="Digite para buscar..."
                        disabled={isParcelado && isParceladoEditMode}
                        size={inputSize}
                        getPopupContainer={modalPopupContainer}
                    >
                       <Input size={inputSize} onFocus={scrollFieldIntoView} />
                    </AutoComplete>
                </Form.Item>
                <Form.Item
                    name="valor"
                    label={isParcelado && isParceladoEditMode ? 'Valor da Parcela' : isParcelado ? 'Valor Total' : 'Valor'}
                    rules={[{ required: true, message: 'Valor é obrigatório' }]}
                >
                    <InputNumber size={inputSize} {...moneyInputProps} onFocus={scrollFieldIntoView} />
                </Form.Item>
                {isParcelado && (
                    <Form.Item
                        name="total_parcelas"
                        label="Número de Parcelas"
                        rules={[
                            { required: true, message: 'Nº de parcelas é obrigatório' },
                            { type: 'number', min: 2, message: 'Deve ser no mínimo 2 parcelas' },
                        ]}
                        extra={<ParceladoValorExtra form={form} isEditMode={isEditMode} />}
                    >
                        <InputNumber min={2} style={{ width: '100%' }} placeholder="Ex: 10" disabled={isEditMode} size={inputSize} inputMode="numeric" onFocus={scrollFieldIntoView} />
                    </Form.Item>
                )}
                <Row gutter={16}>
                    <Col span={isMobile ? 24 : 12}>
                        <Form.Item name="categoria" label="Categoria" rules={[{ required: true, message: 'Categoria é obrigatória' }]}>
                            <Select showSearch placeholder="Selecione..." loading={isLoading} optionFilterProp="children" filterOption={filterOption} size={inputSize} getPopupContainer={modalPopupContainer}>
                                {categoriasDespesa.map(c => <Option key={c.id} value={c.id}>{c.nome}</Option>)}
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col span={isMobile ? 24 : 12}>
                        <Form.Item name="realizado_por_cartao" label="Portador">
                            <Select showSearch placeholder="Selecione o portador" allowClear optionFilterProp="children" filterOption={filterOption} size={inputSize} getPopupContainer={modalPopupContainer}>
                                <Option value="TITULAR">Titular</Option>
                                {cartoesAdicionais.map(c => <Option key={c.id} value={c.id}>{c.portador}</Option>)}
                            </Select>
                        </Form.Item>
                    </Col>
                </Row>
                <Form.Item
                    name="data"
                    label={isParcelado ? 'Data da Compra' : 'Data do Lançamento'}
                    rules={[{ required: true, message: 'Data é obrigatória' }]}
                >
                    <DatePicker
                        format="DD/MM/YYYY"
                        style={{ width: '100%' }}
                        disabled={isParcelado && isEditMode}
                        size={inputSize}
                        getPopupContainer={modalPopupContainer}
                        onFocus={scrollFieldIntoView}
                    />
                </Form.Item>
                <FaturaField {...faturaFieldProps} isParcelado={isParcelado} />
                {fechadaAlert}
            </>
        );
    };

    const sheetBody = (
        <>
            <Radio.Group 
                value={tipoLancamento} 
                onChange={(e) => setTipoLancamento(e.target.value)} 
                style={{ width: '100%', display: 'flex', marginBottom: 24 }}
                disabled={isEditMode}
            >
                <Radio.Button value="vista" style={{ flex: 1, textAlign: 'center', fontSize: '13px' }}>À Vista</Radio.Button>
                <Radio.Button value="parcelado" style={{ flex: 1, textAlign: 'center', fontSize: '13px' }}>Parcelado</Radio.Button>
                <Radio.Button value="encargo" style={{ flex: 1, textAlign: 'center', fontSize: '13px' }}>Encargos</Radio.Button>
            </Radio.Group>
            
            <Form form={form} layout="vertical" onFinish={onFinish} scrollToFirstError>
                {error && <Alert message={error} type="error" showIcon style={{ marginBottom: 24 }} />}
                {renderFormContent()}
            </Form>
        </>
    );

    if (isMobile) {
        return (
            <MobileLaunchSheet
                open={isOpen}
                title={sheetTitle}
                onCancel={handleCancel}
                onSave={() => form.submit()}
                saveLabel="Salvar"
                saveLoading={isLoading}
                saveDisabled={isSavingAndNew}
                cancelDisabled={saveBusy}
                secondaryAction={
                    !isEditMode ? (
                        <Button
                            type="default"
                            loading={isSavingAndNew}
                            onClick={handleSaveAndNew}
                            disabled={isLoading}
                            block
                            size="large"
                        >
                            Salvar e Adicionar Outro
                        </Button>
                    ) : null
                }
            >
                {sheetBody}
            </MobileLaunchSheet>
        );
    }

    return (
        <Modal 
            open={isOpen} 
            title={sheetTitle}
            onCancel={handleCancel} 
            destroyOnClose 
            width={modalWidth}
            footer={[
                <Button key="back" onClick={handleCancel} disabled={saveBusy}>Cancelar</Button>,
                !isEditMode && (
                    <Button key="saveAndNew" type="default" loading={isSavingAndNew} onClick={handleSaveAndNew} disabled={isLoading}>
                        Salvar e Adicionar Outro
                    </Button>
                ),
                <Button key="submit" type="primary" loading={isLoading} onClick={() => form.submit()} disabled={isSavingAndNew}>
                    {isEditMode ? 'Salvar Alterações' : 'Salvar'}
                </Button>,
            ]}
        >
            <Radio.Group 
                value={tipoLancamento} 
                onChange={(e) => setTipoLancamento(e.target.value)} 
                style={{ marginBottom: 24, textAlign: 'center', width: '100%' }}
                disabled={isEditMode}
            >
                <Radio.Button value="vista">À Vista</Radio.Button>
                <Radio.Button value="parcelado">Parcelado</Radio.Button>
                <Radio.Button value="encargo">Encargos</Radio.Button>
            </Radio.Group>
            
            <Form form={form} layout="vertical" onFinish={onFinish} scrollToFirstError>
                {error && <Alert message={error} type="error" showIcon style={{ marginBottom: 24 }} />}
                {renderFormContent()}
            </Form>
        </Modal>
    );
};

export default CartaoDespesaModal;